import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { mockDashboardStats } from '../lib/mockData';
import type { DashboardStats } from '../types';

export function useDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchStats() {
      setLoading(true);
      setError(null);

      try {
        if (!isSupabaseConfigured) {
          if (isMounted) {
            setStats(mockDashboardStats);
            setLoading(false);
          }
          return;
        }

        if (!isMounted) return;

        // Compute stats from catalogue_items and categories counts
        const [itemsResult, categoriesResult] = await Promise.all([
          supabase.from('catalogue_items').select('id, updated_at, confidence_score', { count: 'exact' }),
          supabase.from('categories').select('id', { count: 'exact' }),
        ]);

        if (!isMounted) return;

        const totalItems = itemsResult.count ?? 0;
        const totalCategories = categoriesResult.count ?? 0;
        const today = new Date().toISOString().split('T')[0];
        const updatedToday = (itemsResult.data ?? []).filter(
          (i) => i.updated_at && i.updated_at.startsWith(today)
        ).length;
        const scores = (itemsResult.data ?? [])
          .map((i) => i.confidence_score)
          .filter((s) => s != null) as number[];
        const avgConfidence = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const updatedThisWeek = (itemsResult.data ?? []).filter(
          (i) => i.updated_at && i.updated_at > weekAgo
        ).length;

        setStats({
          total_items: totalItems,
          updated_today: updatedToday,
          updated_this_week: updatedThisWeek,
          avg_confidence: avgConfidence,
          total_categories: totalCategories,
          last_update_time: null,
        });
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch stats');
          // Fallback to mock data on error
          setStats(mockDashboardStats);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  return { stats, loading, error };
}
